import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react"
import {
	Typography,
	Button,
	Upload,
	message,
	Space,
	Tabs,
	Table,
	Dropdown,
	Menu,
	Progress,
	DatePicker,
	Empty,
	AutoComplete,
} from "antd"
import { UploadOutlined, PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, DownOutlined } from "@ant-design/icons"
import apiConstants from "../../api"
import axios from "axios"
import "./style.css"
import { GlobalStoreContext } from "../../Store"

const { Title, Text } = Typography
const { TabPane } = Tabs

// Removed initialState as we're using GlobalStoreContext for authentication

const UploadAndMapStockists = () => {
	// Get global context functions and state
	const { getStockistEntries, failMessage, authToken } = useContext(GlobalStoreContext)

	// State variables
	const [selectedClient, setSelectedClient] = useState({ name: "None" })
	const [mappings, setMappings] = useState([
		{ id: 1, file: null, stockistId: null, date: null, uploading: false, progress: 0, uuid: null },
	])
	const [activeTab, setActiveTab] = useState("1")
	const [results, setResults] = useState({ success: [], errors: [] })
	const [stockistOptions, setStockistOptions] = useState([])
	// No longer needed as we get authToken from GlobalStoreContext
	// const [authToken] = useState(initialState.authToken)

	// Create refs to prevent ResizeObserver errors
	const containerRef = useRef(null)
	const debounceTimeoutRef = useRef(null)

	// Debounced state update function to prevent rapid DOM changes
	const debounceSetMappings = useCallback((updateFunction) => {
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current)
		}
		debounceTimeoutRef.current = setTimeout(() => {
			setMappings(updateFunction)
		}, 16) // 60fps = ~16ms
	}, [])

	// Handle stockist search for autocomplete - using useCallback to prevent recreation on every render
	const handleStockistSearch = useCallback(
		async (searchText) => {
			if (!selectedClient || selectedClient.name === "None") return

			try {
				const response = await getStockistEntries({
					client: { ...selectedClient },
					filter: {
						text: searchText,
					},
				})

				if (response && Array.isArray(response)) {
					setStockistOptions(
						response.map((stockistId) => ({
							value: stockistId,
							label: stockistId,
						}))
					)
				}
			} catch (error) {
				console.error("Error searching stockists:", error)
				setStockistOptions([])
				failMessage(error.message || "Failed to search stockists")
			}
		},
		[selectedClient, getStockistEntries, failMessage]
	)

	// Handle client selection change - using useCallback to prevent re-creation on every render
	const handleClientChange = useCallback((e) => {
		setSelectedClient(apiConstants.SSE_CLIENTS.find((c) => c.name === e.key.split("_")[0]))
	}, [])

	// Client dropdown menu - memoized to prevent unnecessary re-renders
	const clientMenu = useMemo(() => (
		<Menu>
			{apiConstants.SSE_CLIENTS.map((e, idx) => (
				<Menu.Item key={e.name + "_" + idx} onClick={handleClientChange}>
					{e.name}
				</Menu.Item>
			))}
		</Menu>
	), [handleClientChange])

	// Add a new row for mapping
	const addNewRow = () => {
		setMappings((prevMappings) => {
			const newId = prevMappings.length > 0 ? Math.max(...prevMappings.map((m) => m.id)) + 1 : 1
			const newMapping = {
				id: newId,
				file: null,
				stockistId: null,
				date: null,
				uploading: false,
				progress: 0,
				uuid: null,
			}
			const newMappings = [...prevMappings, newMapping]
			console.log("mappings after adding new:", newMappings, "new mapping id:", newId)
			return newMappings
		})
	}

	// Remove a mapping row
	const removeRow = (id) => {
		if (mappings.length > 1) {
			setMappings((prevMappings) => {
				const newMappings = prevMappings.filter((mapping) => mapping.id !== id)
				console.log("mappings after deletion:", newMappings, "deleted id:", id)
				return newMappings
			})
		} else {
			message.warning("At least one mapping is required")
		}
	}

	// Reset a file upload for a mapping row
	const resetFileUpload = (e, mappingId) => {
		// Prevent event propagation to avoid triggering other handlers
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { 
					...mapping, 
					file: null, 
					uploading: false, 
					progress: 0, 
					uuid: null, 
					showUuid: false,
					duplicateFile: false,
					duplicateUuid: null
				} : mapping
			)
			return newMappings
		})
	}

	// Handle file upload for a specific row
	const handleFileChange = async (info, mappingId) => {
		// If this is a remove event, ignore it - we'll handle file removal separately
		if (info.file.status === 'removed') {
			return;
		}
		
		const file = info.file

		// Ensure a client is selected
		if (selectedClient.name === "None") {
			message.error("Please select a client first")
			return
		}

		// Check file type
		const allowedTypes = [
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
			"application/vnd.ms-excel", // xls
			"application/vnd.ms-excel.sheet.binary.macroEnabled.12", // xlsb
			"text/csv", // csv
			"text/html", // html, htm
			"application/pdf", // pdf
			"text/plain", // txt
		]

		if (!allowedTypes.includes(file.type)) {
			message.error(`${file.name} is not a supported file type`)
			return
		}

		// Update the specific mapping row with file information
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { ...mapping, file, uploading: true, progress: 0 } : mapping
			)
			console.log("mappings after file selection:", newMappings, "file:", file.name, "mappingId:", mappingId)
			return newMappings
		})

		// Prepare form data for upload
		const formData = new FormData()
		formData.append("file", file)

		try {
			// Use the client-specific host URL
			const uploadEndpoint = `${selectedClient.hostUrl}/AdminUtility/v0.1/upload-file`

			// Log client and endpoint information for debugging
			console.log(`Uploading file for client: ${selectedClient.name} to endpoint: ${uploadEndpoint}`);

			// Upload the file with progress tracking
			const response = await axios.post(uploadEndpoint, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: authToken,
				},
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

					// Use debounced update for progress to prevent rapid DOM changes
					debounceSetMappings((prevMappings) => {
						const newMappings = prevMappings.map((mapping) =>
							mapping.id === mappingId ? { ...mapping, progress: percentCompleted } : mapping
						)
						console.log(
							"mappings after progress update:",
							newMappings,
							"progress:",
							percentCompleted,
							"mappingId:",
							mappingId
						)
						return newMappings
					})
				},
			})

			// Log complete response data for debugging
			console.log(`Upload response for ${selectedClient.name}:`, response.data);
			console.log(`UUID from response: ${response.data?.file?.uuid}`);

			// Handle successful upload
			if (response.data.duplicateOf) {
				message.warning(
					`File ${file.name} is a duplicate of an existing file: ${response.data.duplicateOf.uuid}`,
					10
				)
				setMappings((prevMappings) => {
					const newMappings = prevMappings.map((mapping) =>
						mapping.id === mappingId ? { 
							...mapping, 
							uploading: false, 
							file, 
							progress: 0,
							duplicateFile: true,
							duplicateUuid: response.data.duplicateOf.uuid
						} : mapping
					)
					console.log("mappings after duplicate handling:", newMappings, "mappingId:", mappingId)
					return newMappings
				})
			} else {
				message.success(`${file.name} uploaded successfully`)
				
				// Extract UUID from response - log the data to debug the issue
				const fileUuid = response.data?.file?.uuid;
				console.log("File UUID from response:", fileUuid);
				console.log("Complete response data:", response.data);
				
				// Ensure we have a valid UUID before updating the state
				if (!fileUuid) {
					console.warn("Warning: No UUID found in file response, attempting to find it elsewhere in the response");
				}
				
				setMappings((prevMappings) => {
					const newMappings = prevMappings.map((mapping) =>
						mapping.id === mappingId
							? {
									...mapping,
									uploading: false,
									progress: 100,
									// Fix for Alkem - Try to get UUID from different places in response
									uuid: fileUuid || response.data?.uuid || null,
									showUuid: true
							  }
							: mapping
					)
					console.log(
						"mappings after upload complete:",
						newMappings,
						"mappingId:",
						mappingId
					)
					return newMappings
				})
			}
		} catch (error) {
			console.error("Upload error:", error)
			message.error(`Failed to upload ${file.name}: ${error.response?.data?.message || "Unknown error"}`)

			setMappings((prevMappings) => {
				const newMappings = prevMappings.map((mapping) =>
					mapping.id === mappingId ? { ...mapping, uploading: false, file: null, progress: 0 } : mapping
				)
				console.log("mappings after upload error reset:", newMappings, "mappingId:", mappingId)
				return newMappings
			})
		}
	}

	// Handle stockist selection for a row
	const handleStockistChange = (value, mappingId) => {
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { ...mapping, stockistId: String(value) } : mapping
			)
			console.log("mappings after stockist update:", newMappings, "value:", value, "mappingId:", mappingId)
			return newMappings
		})
	}

	// Handle date selection for a row
	const handleDateChange = (date, mappingId) => {
		setMappings((prevMappings) => {
			const newMappings = prevMappings.map((mapping) =>
				mapping.id === mappingId ? { ...mapping, date } : mapping
			)
			console.log("mappings after date update:", newMappings, "date:", date, "mappingId:", mappingId)
			return newMappings
		})
	}

	// Submit all mappings
	const handleSubmit = async () => {
		// Validate all mappings
		const invalidMappings = mappings.filter((mapping) => !mapping.uuid || !mapping.stockistId)
		console.log("mappings before submission:", mappings)
		console.log("Client name:", selectedClient.name)
		console.log("Invalid mappings:", invalidMappings)
		console.log("Submit button disabled condition:", selectedClient.name === "None" || mappings.some((m) => !m.uuid || !m.stockistId))

		if (invalidMappings.length > 0) {
			message.error("All rows must have both a file and a stockist selected")
			return
		}

		if (selectedClient.name === "None") {
			message.error("Please select a client first")
			return
		}

		// Prepare request payload
		const payload = {
			mappings: mappings.map((mapping) => ({
				uuid: mapping.uuid,
				stockistId: mapping.stockistId,
				date: mapping.date ? mapping.date.toISOString() : undefined,
			})),
		}

		try {
			// Use the client-specific host URL
			const mapEndpoint = `${selectedClient.hostUrl}/AdminUtility/v0.1/bulk-map-stockists`

			const response = await axios.post(mapEndpoint, payload, {
				headers: { Authorization: authToken },
			})

			if (response.data.ok) {
				message.success("Mappings submitted successfully")
				setResults(response.data.results)
				setActiveTab("2") // Switch to results tab
			} else {
				message.error("Failed to submit mappings")
			}
		} catch (error) {
			console.error("Submission error:", error)
			message.error(`Failed to submit mappings: ${error.response?.data?.message || "Unknown error"}`)
		}
	}

	// Results tab columns
	const successColumns = [
		{
			title: "File",
			dataIndex: "uuid",
			key: "uuid",
			render: (uuid) => {
				const mapping = mappings.find((m) => m.uuid === uuid)
				return mapping?.file?.name || "Unknown file"
			},
		},
		{
			title: "UUID",
			dataIndex: "uuid",
			key: "file_uuid",
			render: (uuid) => (
				<Text copyable style={{ fontSize: '12px' }}>{uuid}</Text>
			),
		},
		{
			title: "Stockist ID",
			dataIndex: "stockistId",
			key: "stockistId",
		},
		{
			title: "Status",
			key: "status",
			render: () => (
				<Text type="success">
					<CheckCircleOutlined /> Success
				</Text>
			),
		},
	]

	const errorColumns = [
		{
			title: "File",
			dataIndex: "uuid",
			key: "uuid",
			render: (uuid) => {
				const mapping = mappings.find((m) => m.uuid === uuid)
				return mapping?.file?.name || uuid
			},
		},
		{
			title: "Stockist ID",
			dataIndex: "stockistId",
			key: "stockistId",
		},
		{
			title: "Error",
			dataIndex: "error",
			key: "error",
		},
		{
			title: "Status",
			key: "status",
			render: () => (
				<Text type="danger">
					<CloseCircleOutlined /> Failed
				</Text>
			),
		},
	]

	// Effect to initialize stockist search when client changes
	useEffect(() => {
		if (selectedClient.name !== "None") {
			// Reset stockist options
			setStockistOptions([])

			// Reset stockist IDs in mappings when client changes
			setMappings((prevMappings) => {
				const newMappings = prevMappings.map((mapping) => ({
					...mapping,
					stockistId: null,
				}))
				console.log("Reset stockist IDs after client change:", newMappings)
				return newMappings
			})

			// Initialize with an empty search to get some stockists
			handleStockistSearch("")
		}
	}, [selectedClient, handleStockistSearch])


	return (
		<div className="upload-map-container" ref={containerRef}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Upload & Map" key="1">
					<Space direction="vertical" size="large" className="full-width">
						<div className="client-selector">
							<Text strong>Target Client: {selectedClient.name}</Text>
							<Dropdown overlay={clientMenu}>
								<Button type="link" className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
									Change Client <DownOutlined />
								</Button>
							</Dropdown>
						</div>

						<Title level={4}>File-Stockist Mapping</Title>

						<div className="mapping-container">
							{mappings.map((mapping) => (
								<div key={mapping.id} className="mapping-row">
									<div className="file-upload">
										<Upload
											beforeUpload={() => false}
											onChange={(info) => handleFileChange(info, mapping.id)}
											fileList={mapping.file ? [mapping.file] : []}
											disabled={mapping.uploading}
											showUploadList={false}
										>
											<Button
												icon={<UploadOutlined />}
												disabled={mapping.uploading}
											>
												Select File
											</Button>
										</Upload>
										{mapping.uploading && <Progress percent={mapping.progress} size="small" />}
										{mapping.file && !mapping.uploading && (
											<div>
												<Text ellipsis className="filename">
													{mapping.file.name}
												</Text>
												
												{/* Show file UUID if available */}
												{mapping.uuid && mapping.showUuid && (
													<div>
														<Text type="secondary" style={{ fontSize: '12px' }}>
															UUID: {mapping.uuid}
														</Text>
													</div>
												)}
												
												{/* Show duplicate file information */}
												{mapping.duplicateFile && mapping.duplicateUuid && (
													<div>
														<Text type="warning" style={{ fontSize: '12px' }}>
															Duplicate of: {mapping.duplicateUuid}
														</Text>
													</div>
												)}
												
												{/* Add reset button for duplicate or error cases */}
												{(mapping.duplicateFile || mapping.uuid) && (
													<Button 
														type="link" 
														size="small"
														style={{ padding: '0', marginTop: '4px' }}
														onClick={(e) => resetFileUpload(e, mapping.id)}
													>
														Reset file
													</Button>
												)}	
											</div>
										)}
									</div>

									<div className="stockist-selector">
										<AutoComplete
											placeholder="Select a stockist"
											value={mapping.stockistId}
											onChange={(value) => handleStockistChange(value, mapping.id)}
											onSearch={handleStockistSearch}
											options={stockistOptions}
											disabled={selectedClient.name === "None"}
											style={{ width: "100%" }}
										/>
									</div>

									<div className="date-selector">
										<DatePicker
											onChange={(date) => handleDateChange(date, mapping.id)}
											value={mapping.date}
											placeholder="Select date (optional)"
										/>
									</div>

									<div className="row-actions">
										<Button
											type="text"
											danger
											onClick={() => removeRow(mapping.id)}
											disabled={mappings.length === 1}
										>
											Remove
										</Button>
									</div>
								</div>
							))}
						</div>

						<div className="action-buttons">
							<Button type="dashed" icon={<PlusOutlined />} onClick={addNewRow}>
								Add Another Mapping
							</Button>

							<Button
								type="primary"
								onClick={handleSubmit}
								disabled={
									selectedClient.name === "None" || mappings.some((m) => !m.uuid || !m.stockistId)
								}
							>
								Submit All Mappings
							</Button>
						</div>


					</Space>
				</TabPane>

				<TabPane tab="Results" key="2">
					<div className="results-container">
						<Title level={4}>Mapping Results</Title>

						{results.success?.length > 0 || results.errors?.length > 0 ? (
							<>
								<div className="success-section">
									<Title level={5}>Successful Mappings</Title>
									{results.success?.length > 0 ? (
										<Table dataSource={results.success} columns={successColumns} rowKey="uuid" />
									) : (
										<Empty description="No successful mappings" />
									)}
								</div>

								<div className="error-section">
									<Title level={5}>Failed Mappings</Title>
									{results.errors?.length > 0 ? (
										<Table dataSource={results.errors} columns={errorColumns} rowKey="uuid" />
									) : (
										<Empty description="No failed mappings" />
									)}
								</div>
							</>
						) : (
							<Empty description="No results to display. Please submit mappings first." />
						)}

						<div className="action-buttons">
							<Button onClick={() => setActiveTab("1")}>Return to Upload</Button>
						</div>
					</div>
				</TabPane>
			</Tabs>
		</div>
	)
}

export default UploadAndMapStockists
